// @ts-strict-ignore
import React from 'react'
import { path } from '../../utils/obj'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { getValue } from '../../utils/product-schema'
import { Table, TableRow, TableCell } from '../table/platinum-table'
import { Button } from '../../atoms'
import { removeWhitespaceAndSpecialChars } from '../helpers/commons'

interface AggItem {
  key?: string
  keys?: string[]
  label: string
  format: 'percent' | 'bold'
  aggValue: string
  schemaData: object[]
}

const getBucket = (aggFrom: AggItem, buckets: any[]) => {
  return aggFrom.keys
    ? buckets
        .filter((b) => aggFrom.keys!.includes(b.key))
        .sort((a, b) => b.max_score.value - a.max_score.value)[0]
    : buckets.find((b) => b.key === aggFrom.key)
}

const handleValue = (value, aggs, sys, name) => {
  if (value.value) {
    return value.value
  } else if (value.aggFrom) {
    const buckets = aggs.find((a) => a[value.aggFrom.label])[value.aggFrom.label]?.buckets
    const bucket = getBucket(value.aggFrom, buckets)
    return path(value.aggFrom.aggValue.split('.'), bucket)
  } else if (value.ctaLink) {
    /* eslint-disable camelcase */
    const trackingObject = {
      component_category: 'eevee-component',
      component_id: sys?.id,
      component_name: 'aggregationTable',
      contentful_name: name,
      contentful_variant: null,
      additionalProps: { text: value.ctaLink.text, url: value.ctaLink.value },
    }
    return (
      <>
        <div className={'button--desktop'}>
          <Button href={value.ctaLink.value} dataClickTracking={trackingObject} size='md'>
            {value.ctaLink.text}
          </Button>
        </div>

        <div className={'PlatinumTable__ctaButton button--mobile'}>
          <Button variant='icon' href={value.ctaLink.value} dataClickTracking={trackingObject} />
        </div>
      </>
    )
  }
}

const handleFormat = (value, format) => {
  let formattedValue = value
  if (!isNaN(value)) {
    formattedValue = value.toFixed(2)
  }
  if (format === 'bold') {
    return <b>{formattedValue}</b>
  } else if (format === 'percent') {
    return `${formattedValue}%`
  } else {
    return formattedValue
  }
}

const createSchema = (table, aggs) => {
  const schemaItems = table.reduce((acc, row) => {
    const hasSchema = row.values.find((v) => {
      return !!v?.aggFrom?.schemaData
    })
    if (hasSchema) {
      return acc.concat([hasSchema.aggFrom])
    } else {
      return acc
    }
  }, [])

  const itemListElement = schemaItems.map((aggItem: AggItem, index: number) => {
    const buckets = aggs.find((a) => a[aggItem.label])[aggItem.label]?.buckets
    const bucket = getBucket(aggItem, buckets)
    const data = bucket.by_top_hit.hits.hits[0]
    const obj = { position: index + 1 }
    aggItem.schemaData.forEach((it) => {
      const objValue = Object.values(it)[0]
      const value = getValue(objValue, data)
      const objKey = Object.keys(it)[0]
      obj[objKey] = value
    })
    return obj
  })
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    numberOfItems: itemListElement.length,
    itemListElement: itemListElement,
  }
  return schema
}

const BestRates = (props) => {
  const { contents } = props.grid
  return (
    <div className='BestRates' id={removeWhitespaceAndSpecialChars(props.name)}>
      <Helmet>
        {contents.map((table, index) => {
          try {
            return (
              <script type='application/ld+json' key={index}>
                {JSON.stringify(createSchema(table, props.aggs))}
              </script>
            )
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error creating json schema for agg table', e.message)
          }
        })}
      </Helmet>
      {props.title && <h2 className='PlatinumTable__title'>{props.title}</h2>}
      {props.subheading && <div className='PlatinumTable__subheading'>{props.subheading}</div>}
      {contents.map((table, tableIndex) => (
        <Table key={tableIndex}>
          {table.map(({ type, values }, rowIndex) => {
            const content = (v) =>
              handleFormat(
                handleValue(v, props.aggs, props.sys, props.name),
                v.aggFrom?.format ?? v.format,
              )
            return (
              values.every(
                (v) =>
                  type === 'th' || handleValue(v, props.aggs, props.sys, props.name) !== undefined,
              ) && (
                <TableRow key={rowIndex}>
                  {values.map((v, columnIndex) => (
                    <TableCell key={columnIndex} type={type}>
                      {content(v)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            )
          })}
        </Table>
      ))}
    </div>
  )
}

const BestRatesConnected = connect((state, props) => {
  return {
    ...props.schema,
    aggs: state.elasticsearch.productDataSet[props.dataset.sys.id].data?.aggs,
    title: props.title,
    subheading: props.subheading,
    name: props.name,
    sys: props.sys,
  }
})(BestRates)

export default BestRatesConnected
